import * as React from 'react';
import { styled, useTheme } from '@mui/material';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import LoadingIcon from 'src/frontend-common/components/Icons/Loading';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import { useUpdateResolutionIdRequestMutation, useUpdateScoreRequestMutation } from 'src/redux/services/score';
import GenericForm from 'src/pages/ManualLookup/GenericForm';
import SingleForm from 'src/pages/ManualLookup/SingleForm';
import { AntiPaddingBox } from 'src/style/common';
import AppkeyAutocomplete from 'src/components/AppkeyAutocomplete';
import LoginSimulator from 'src/pages/ManualLookup/LoginSimulator';
import { isAdmin } from 'src/utils/user';
import useCurrentIP from 'src/hooks/useCurrentIP';
import { defaultGenericFormValues, fieldNameTranslator, IGenericFormValues, renderActiveTabContent, singleFormProducts } from 'src/pages/ManualLookup/utils';
import ManualLookupsTabs, { manualLookupsTabs } from 'src/pages/ManualLookup/ManualLookupsTabs';
import { DetailProducts } from 'src/frontend-common/dto/Types/ProductType';
import { useLocation } from 'react-router-dom';
import { selectUser } from 'src/redux/slices/userSlice';
import { useSelector } from 'react-redux';
import { NavigateCallbackFn } from 'src/frontend-common/dto/Interfaces/FrontendLookups';

const LoaderMarginTop = styled(LoadingScreen)(({ theme }) => ({
  marginTop: theme.spacing(5),
}));
// eslint-disable-next-line complexity
const ManualLookup: React.FC<{ navigateTo: NavigateCallbackFn }> = ({ navigateTo }) => {
  const [resolutionId, setResolutionId] = React.useState<string | undefined>('');

  const [selectedAppkey, setSelectedAppkey] = React.useState<{
    label: string;
    key: string;
  } | null>(null);
  const [selectedJSKey, setSelectedJSKey] = React.useState<string>('');
  const [activeTab, setActiveTab] = React.useState<number>(0);

  const [defaultValue, setDefaultValue] = React.useState<string | null>(null);
  const [generalFormInitialValues, setGeneralFormInitialValues] = React.useState<IGenericFormValues>(defaultGenericFormValues);

  const params = useLocation();
  const currentIP = useCurrentIP();

  const theme = useTheme();
  const user = useSelector(selectUser);
  const [updateResolutionId, resolutionData] = useUpdateResolutionIdRequestMutation();
  const [updateScore, { isLoading, isError, error, isSuccess }] = useUpdateScoreRequestMutation();

  React.useEffect(() => {
    if (isSuccess) {
      setGeneralFormInitialValues(defaultGenericFormValues);
    }
  }, [isSuccess]);

  React.useEffect(() => {
    const tabFromSearchString = new URLSearchParams(params.search).get('tab');
    if (tabFromSearchString) {
      if (isNaN(Number(tabFromSearchString)) || Number(tabFromSearchString) < 0 || Number(tabFromSearchString) > 5) {
        setActiveTab(0);
        return;
      }
      setActiveTab(Number(tabFromSearchString));
    }
  }, [params]);

  const anyError = error as any;
  const errorMessage = anyError?.data?.message?.message;

  const handleTabChange = (_event: unknown, newValue: number, dontChangeDefault?: boolean) => {
    if (!dontChangeDefault) {
      setDefaultValue(null);
    }
    setActiveTab(newValue);
    setResolutionId('');
  };

  React.useEffect(() => {
    if (resolutionData.data?.resolution_id) setResolutionId(resolutionData.data?.resolution_id);
  }, [resolutionData.data?.resolution_id]);

  React.useEffect(() => {
    if (isError) setResolutionId('');
  }, [isError]);

  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem('simulator');
    };
  }, []);

  if (currentIP) {
    return (
      <DashboardPageLayout title="Manual Lookup">
        <>
          <AppkeyAutocomplete selectedAppkey={selectedAppkey} setSelectedAppkey={setSelectedAppkey} setSelectedJSKey={setSelectedJSKey} activeOnly={true} />

          <div
            style={{
              backgroundColor: 'white',
              marginTop: theme.spacing(3),
            }}
          >
            <ManualLookupsTabs activeTab={activeTab} handleTabChange={handleTabChange} isAdmin={isAdmin(user)} />
            {selectedAppkey && manualLookupsTabs[activeTab]?.product === 'onboarding' && (
              <GenericForm
                key={selectedAppkey.key}
                apiKey={selectedAppkey.key}
                jsKey={selectedJSKey}
                isLoading={isLoading}
                updateScore={updateScore}
                updateResolutionId={updateResolutionId}
                ip={currentIP}
                setResolutionId={setResolutionId}
                fieldName=""
                changeTab={handleTabChange}
                defaultValue={null}
                setDefaultValue={setDefaultValue}
                generalFormInitialValues={generalFormInitialValues}
                setGeneralFormInitialValues={setGeneralFormInitialValues}
              />
            )}
            {selectedAppkey && singleFormProducts.includes(manualLookupsTabs[activeTab]?.product ?? ('' as DetailProducts)) && (
              <SingleForm
                key={selectedAppkey.key}
                apiKey={selectedAppkey.key}
                jsKey={selectedJSKey}
                isLoading={isLoading}
                updateScore={updateScore}
                updateResolutionId={updateResolutionId}
                ip={currentIP}
                setResolutionId={setResolutionId}
                fieldName={fieldNameTranslator(manualLookupsTabs[activeTab]?.product || '')}
                changeTab={handleTabChange}
                defaultValue={defaultValue}
                setDefaultValue={setDefaultValue}
                generalFormInitialValues={generalFormInitialValues}
                setGeneralFormInitialValues={setGeneralFormInitialValues}
              />
            )}
            {selectedAppkey && manualLookupsTabs[activeTab]?.product === 'login' && (
              <LoginSimulator frontEndAppKey={selectedJSKey} backEndAppKey={selectedAppkey.key} navigateTo={navigateTo} />
            )}
          </div>

          {resolutionData.isLoading && <LoaderMarginTop variant="circle" />}
          {!resolutionData.isLoading && isError && !resolutionId?.includes('MOCK') && (
            <FailureScreen>{errorMessage || 'Api Key invalid or not enabled'}</FailureScreen>
          )}
          {((!resolutionData.isLoading && !isError && resolutionId && resolutionId === resolutionData.data?.resolution_id) ||
            resolutionId?.includes('MOCK')) && <AntiPaddingBox>{renderActiveTabContent(activeTab, resolutionId, navigateTo)}</AntiPaddingBox>}
        </>
      </DashboardPageLayout>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: '25%' }}>
      <LoadingIcon />
    </div>
  );
};

export default ManualLookup;
